import React, { useState } from 'react';
import { HardwareType, ModelReferenceCreate } from '../../services/typesService';
import { useI18n } from '../../contexts/I18nContext';
import './AddModelReferenceModal.css';

interface AddModelReferenceModalProps {
  isOpen: boolean;
  hardwareTypes: HardwareType[];
  onClose: () => void;
  onCreated: () => void;
  onCreate: (hardwareTypeId: number, data: ModelReferenceCreate) => Promise<void>;
}

interface FormData {
  hardwareTypeId: number;
  code: string;
  name: string;
  description: string;
}

interface FormErrors {
  [key: string]: string;
}

export const AddModelReferenceModal: React.FC<AddModelReferenceModalProps> = ({
  isOpen,
  hardwareTypes,
  onClose,
  onCreated,
  onCreate
}) => {
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({
    hardwareTypeId: 0,
    code: '',
    name: '',
    description: ''
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.hardwareTypeId) {
      newErrors.hardwareTypeId = 'Hardware type is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Reference code is required';
    } else if (!/^[A-Z0-9-]{2,20}$/.test(formData.code)) {
      newErrors.code = 'Code must be 2-20 uppercase letters, numbers, or dashes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onCreate(formData.hardwareTypeId, {
        code: formData.code.trim(),
        name: formData.name.trim() || `${formData.code.trim()} Revision`,
        description: formData.description.trim() || undefined
      });
      onCreated();
      resetForm();
      onClose();
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create model reference';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ hardwareTypeId: 0, code: '', name: '', description: '' });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const activeHardwareTypes = hardwareTypes.filter(t => t.is_active);

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="add-model-ref-modal" data-testid="modal-add-model-reference" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <div className="header-icon">
              <span>+</span>
            </div>
            <h2>{t('deviceTypes.addModelReference')}</h2>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Banner */}
        {errors.submit && (
          <div className="error-banner">
            <div className="error-content">
              <span className="error-icon">!</span>
              <span>{errors.submit}</span>
            </div>
            <button onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            {/* Section: Hardware Type Selection */}
            <div className="form-section">
              <h3 className="section-title">{t('deviceTypes.hardwareTypes').slice(0, -1)}</h3>
              <p className="section-description">
                Select the hardware type this model reference belongs to.
              </p>

              <div className="form-fields">
                <div className="form-field">
                  <label className="required">{t('deviceTypes.hardwareTypes').slice(0, -1)}</label>
                  <select
                    value={formData.hardwareTypeId || ''}
                    data-testid="field-modelref-hardware-type"
                    onChange={(e) => handleInputChange('hardwareTypeId', Number(e.target.value))}
                    className={`field-select ${errors.hardwareTypeId ? 'error' : ''}`}
                    disabled={isSubmitting}
                  >
                    <option value="">{t('configTemplates.selectHardwareTypePlaceholder')}</option>
                    {activeHardwareTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} ({type.code})
                      </option>
                    ))}
                  </select>
                  {errors.hardwareTypeId && <span className="field-error">{errors.hardwareTypeId}</span>}
                </div>
              </div>
            </div>

            {/* Section: Reference Details */}
            <div className="form-section">
              <h3 className="section-title">{t('deviceTypes.referenceDetails')}</h3>
              <p className="section-description">
                Define the model reference code and name for this hardware revision.
              </p>

              <div className="form-fields">
                <div className="form-field">
                  <label className="required">{t('deviceTypes.referenceCode')}</label>
                  <input
                    type="text"
                    data-testid="field-modelref-code"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    className={`field-input ${errors.code ? 'error' : ''}`}
                    placeholder="e.g., PGA-04, IOTL-V2"
                    maxLength={20}
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">Uppercase letters, numbers, and dashes (2-20 chars)</span>
                  {errors.code && <span className="field-error">{errors.code}</span>}
                </div>

                <div className="form-field">
                  <label>{t('deviceTypes.name')}</label>
                  <input
                    type="text"
                    data-testid="field-modelref-name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="field-input"
                    placeholder="e.g., PGA Gateway Rev 4"
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">Human-readable name (optional)</span>
                </div>

                <div className="form-field full-width">
                  <label>{t('common.description')}</label>
                  <textarea
                    value={formData.description}
                    data-testid="field-modelref-description"
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="field-textarea"
                    placeholder={t('deviceTypes.modelRefDescriptionPlaceholder')}
                    rows={3}
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">Optional - describe the hardware revision or variant</span>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <div className="footer-left">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                data-testid="btn-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
            <div className="footer-right">
              <button
                type="submit"
                className="btn btn-primary"
                data-testid="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Creating...
                  </>
                ) : (
                  'Create Model Reference'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
